/*
 * Foundation theme: <product-tour>. A scroll-scrubbed product tour (see
 * sections/product-tour.liquid). Progressive enhancement only: without this
 * script, with reduced motion, with motion off in Theme settings, or with
 * Data Saver on, the section stays a static stack of image and callouts.
 *
 * With motion: the stage sticks while the section scrolls past, and the scroll
 * position becomes a progress value 0–1. Each layer maps its own slice of that
 * progress to a scale / move / rotate / fade; each callout shows inside its slice;
 * an optional sprite sheet is drawn to a canvas, one frame per step of progress.
 * Scrolling stays native (no scroll-jacking). Progress is eased towards the real
 * value each frame so wheel steps don't jump.
 */
(() => {
  const html = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || {};
  const motionOk = () => html.classList.contains('motion-ok') && !reduce.matches && !connection.saveData;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const num = (element, name, fallback) => {
    const value = parseFloat(element.dataset[name]);
    return Number.isFinite(value) ? value : fallback;
  };

  class ProductTour extends HTMLElement {
    connectedCallback() {
      if (!motionOk() || !('IntersectionObserver' in window)) return;
      this.stage = this.querySelector('[data-tour-stage]');
      this.layers = Array.from(this.querySelectorAll('[data-tour-layer]')).map((element) => ({
        element,
        start: num(element, 'start', 0) / 100,
        end: num(element, 'end', 100) / 100,
        scale: [num(element, 'sf', 100) / 100, num(element, 'st', 100) / 100],
        x: [num(element, 'xf', 0), num(element, 'xt', 0)],
        y: [num(element, 'yf', 0), num(element, 'yt', 0)],
        rotate: [num(element, 'rf', 0), num(element, 'rt', 0)],
        // "Zoom on phones": a multiplier applied to the zoom on narrow screens.
        phone: num(element, 'pz', 100) / 100,
        shown: null,
      }));
      this.callouts = Array.from(this.querySelectorAll('[data-tour-callout]')).map((element) => ({
        element,
        start: num(element, 'start', 0) / 100,
        end: num(element, 'end', 100) / 100,
      }));
      this.cta = this.querySelector('[data-tour-cta]');
      this.ctaFrom = this.cta ? num(this.cta, 'from', 85) / 100 : 1;
      this.initSprite();
      this.progress = 0;
      this.target = 0;
      this.frame = 0;
      this.measure = () => {
        const rect = this.getBoundingClientRect();
        this.top = rect.top + window.scrollY;
        this.range = Math.max(1, rect.height - window.innerHeight);
        // Phones show callouts under the product, so sideways offsets are dropped there.
        this.xScale = window.innerWidth < 750 ? 0 : 1;
        if (this.sprite) this.sizeSprite();
      };
      this.onScroll = () => {
        this.target = clamp((window.scrollY - this.top) / this.range, 0, 1);
        if (!this.frame) this.frame = window.requestAnimationFrame(this.tick);
      };
      this.tick = () => {
        this.frame = 0;
        // Ease towards the real progress; snap when close so the loop can stop.
        const delta = this.target - this.progress;
        this.progress = Math.abs(delta) < 0.0008 ? this.target : this.progress + delta * 0.22;
        this.render(this.progress);
        if (this.progress !== this.target) this.frame = window.requestAnimationFrame(this.tick);
      };
      this.onResize = () => {
        this.measure();
        this.onScroll();
      };
      this.classList.add('is-enhanced');
      this.measure();
      this.render(0);
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('load', this.onResize, { once: true });
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.onResize();
        } else {
          window.removeEventListener('scroll', this.onScroll);
        }
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (!this.observer) return;
      this.observer.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.cancelAnimationFrame(this.frame);
    }

    render(p) {
      this.style.setProperty('--tour-p', p.toFixed(4));
      this.layers.forEach((layer) => {
        const span = Math.max(0.0001, layer.end - layer.start);
        const t = clamp((p - layer.start) / span, 0, 1);
        // Visible only inside its slice, with a short fade at each inner edge.
        const fadeIn = layer.start <= 0 ? 1 : clamp(t / 0.15, 0, 1);
        const fadeOut = layer.end >= 1 ? 1 : clamp((1 - t) / 0.15, 0, 1);
        const inside = p >= layer.start - 0.02 && p <= layer.end + 0.02;
        const opacity = inside ? Math.min(fadeIn, fadeOut) : 0;
        const shown = opacity > 0;
        if (shown !== layer.shown) {
          layer.element.classList.toggle('is-shown', shown);
          layer.shown = shown;
        }
        if (!shown) return;
        const mix = (pair) => pair[0] + (pair[1] - pair[0]) * t;
        const style = layer.element.style;
        style.setProperty('--s', (mix(layer.scale) * (this.xScale === 0 ? layer.phone : 1)).toFixed(4));
        style.setProperty('--x', (mix(layer.x) * this.xScale).toFixed(2));
        style.setProperty('--y', mix(layer.y).toFixed(2));
        style.setProperty('--r', mix(layer.rotate).toFixed(2));
        style.setProperty('--o', opacity.toFixed(3));
      });
      this.callouts.forEach((callout) => {
        callout.element.classList.toggle('is-active', p >= callout.start && p <= callout.end);
      });
      if (this.cta) this.cta.classList.toggle('is-active', p >= this.ctaFrom);
      if (this.sprite) this.drawSprite(p);
    }

    /* ---------- Optional sprite sheet (a grid of frames in one image) ---------- */
    initSprite() {
      const canvas = this.querySelector('[data-tour-sprite]');
      if (!canvas || !canvas.getContext) return;
      const frames = num(canvas, 'frames', 0);
      const columns = num(canvas, 'columns', 1);
      if (frames < 2 || columns < 1 || !canvas.dataset.src) return;
      this.sprite = {
        canvas,
        context: canvas.getContext('2d'),
        frames,
        columns,
        rows: Math.ceil(frames / columns),
        start: num(canvas, 'start', 0) / 100,
        end: num(canvas, 'end', 100) / 100,
        image: null,
        last: -1,
      };
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        this.sprite.image = image;
        this.sprite.frameWidth = image.naturalWidth / columns;
        this.sprite.frameHeight = image.naturalHeight / this.sprite.rows;
        this.sprite.last = -1;
        this.sizeSprite();
        this.drawSprite(this.progress || 0);
      };
      image.src = canvas.dataset.src;
    }

    sizeSprite() {
      const { canvas } = this.sprite;
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      const width = Math.round(canvas.clientWidth * ratio);
      const height = Math.round(canvas.clientHeight * ratio);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        this.sprite.last = -1;
      }
    }

    drawSprite(p) {
      const sprite = this.sprite;
      if (!sprite.image) return;
      const span = Math.max(0.0001, sprite.end - sprite.start);
      const t = clamp((p - sprite.start) / span, 0, 1);
      const index = Math.round(t * (sprite.frames - 1));
      if (index === sprite.last) return;
      sprite.last = index;
      const { canvas, context, frameWidth, frameHeight } = sprite;
      const sx = (index % sprite.columns) * frameWidth;
      const sy = Math.floor(index / sprite.columns) * frameHeight;
      // Fit the frame inside the canvas, centred (like object-fit: contain).
      const scale = Math.min(canvas.width / frameWidth, canvas.height / frameHeight);
      const dw = frameWidth * scale;
      const dh = frameHeight * scale;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(sprite.image, sx, sy, frameWidth, frameHeight, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
    }

    /* Theme editor: jump to where a selected callout or layer starts. */
    showBlock(block) {
      if (!this.range) return;
      const start = num(block, 'start', 0) / 100;
      window.scrollTo({ top: this.top + start * this.range + 1, behavior: 'auto' });
    }
  }

  if (!customElements.get('product-tour')) customElements.define('product-tour', ProductTour);

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest && event.target.closest('[data-tour-layer], [data-tour-callout]');
    const tour = block && block.closest('product-tour');
    if (tour && tour.showBlock) tour.showBlock(block);
  });
})();
